import i18next from "config/i18next";
import { FrequencyType } from "pages/slots/slotsTemplates/templateCreationModal/types";
import { TemplateDays } from "sagas/types/slotTemplates";

export const WEEK_DAYS_NAMES = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;
export type WeekdaysType = typeof WEEK_DAYS_NAMES[number];

export class SlotsConstants {
  public static STATUS_CREATED = "CREATED";
  public static STATUS_IN_PROGRESS = "IN_PROGRESS";
  public static STATUS_CANCELLED = "CANCELED";
  public static STATUS_EXPIRED = "EXPIRED";

  public static DISABLED_PICKER_ERROR = 10040;
  public static PICKER_NOT_AVAILABLE_ERROR = 50002;
  public static CANCELLED_SLOT_ERROR = 50003;
  public static EXPIRED_SLOT_ERROR = 50004;
  public static INVALID_REGION_ERROR = 9000;
  public static DATE_TIME_SLOT_NOT_ALLOWED_ERROR = 50009;
  public static TEMPLATE_OVERLAP_ERROR = 50010;

  public static SLOT_MAX_CAPACITY = 9999;

  private static DAYS: { id: WeekdaysType; name: string }[] = [
    { id: "mon", name: "monday" },
    { id: "tue", name: "tuesday" },
    { id: "wed", name: "wednesday" },
    { id: "thu", name: "thursday" },
    { id: "fri", name: "friday" },
    { id: "sat", name: "saturday" },
    { id: "sun", name: "sunday" },
  ];

  public static FULL_WEEK_DAYS = this.DAYS.map((day) => ({
    id: day.id,
    name: day.name,
  }));

  public static isCancellableSlot(status: string) {
    return [this.STATUS_CREATED, this.STATUS_IN_PROGRESS].includes(status);
  }

  public static isInvalidSlotStatus(errorCode: number) {
    return [this.CANCELLED_SLOT_ERROR, this.EXPIRED_SLOT_ERROR].includes(
      errorCode
    );
  }

  public static getFrequencyRange(days: boolean[]) {
    if (!days) return "";

    const numberOfDays = days.filter((day) => day).length;
    const firstTrueIdx = days.indexOf(true);

    if (numberOfDays === 1) {
      return i18next.t(`global:${this.FULL_WEEK_DAYS[firstTrueIdx].name}`);
    }

    const lastTrueIdx = days.lastIndexOf(true);
    const hasEmptySpaces = days
      .slice(firstTrueIdx, lastTrueIdx)
      .some((day) => !day);
    if (hasEmptySpaces || numberOfDays === 2) {
      return days
        .map((day, idx) => (day ? i18next.t(`global:${this.FULL_WEEK_DAYS[idx].name}Short`) : null))
        .filter((day) => day)
        .join(", ");
    }

    return `${i18next.t(`global:${this.FULL_WEEK_DAYS[firstTrueIdx].name}Short`)} - ${i18next.t(`global:${this.FULL_WEEK_DAYS[lastTrueIdx].name}Short`)}`;
  }

  public static getErrorMessage(errorCode: number) {
    return errorCode === this.DISABLED_PICKER_ERROR
      ? i18next.t("slotDetail:disabledPickerError")
      : errorCode === this.PICKER_NOT_AVAILABLE_ERROR
      ? i18next.t("slotDetail:pickerNotAvailableError")
      : errorCode === this.INVALID_REGION_ERROR
      ? i18next.t("slotDetail:invalidRegionError")
      : null;
  }

  public static getWeekName(dayID: string) {
    const daySelected = this.FULL_WEEK_DAYS.find((day) => day.id === dayID);
    return daySelected ? i18next.t(`global:${daySelected.name}`) : null;
  }

  public static formatFrequencies(frequency: FrequencyType): TemplateDays {
    return WEEK_DAYS_NAMES.reduce(
      (acc, curr) => ({
        ...acc,
        [curr]: {
          capacity: frequency[curr] ?? null,
        },
      }),
      {} as TemplateDays
    );
  }
}
